/**
 * Zod schemas — œuvres : filtres URL (`useListFilters`) + formulaire création.
 *
 * Le BE est source de vérité (Pydantic + SQL). Ces schémas FE ne sont là
 * que pour valider les inputs utilisateur côté client (avant envoi) et
 * pour décrire les filtres URL au hook `useListFilters`. Tout mismatch
 * BE/FE remonterait via le codegen OpenAPI (`gen:api:check`).
 */

import { z } from 'zod'

// ──────────────────────────────────────────────────────────────────────────────
// Filtres URL — `useListFilters(workListFiltersSchema)` sur WorksPage
// ──────────────────────────────────────────────────────────────────────────────

const workStatusEnum = z.enum(['draft', 'published', 'archived'])
const workOrientationEnum = z.enum(['portrait', 'landscape', 'square'])
const workSortEnum = z.enum(['relevance', 'recent', 'popular', 'engagement'])

/** Coerce un string ou string[] en array d'entiers. */
const tagsSchema = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform((v) => {
    if (v === undefined) return undefined
    const arr = Array.isArray(v) ? v : [v]
    const ints = arr.map((s) => Number.parseInt(s, 10)).filter((n) => Number.isFinite(n))
    return ints.length > 0 ? ints : undefined
  })

const idSchema = z
  .string()
  .optional()
  .transform((v) => {
    if (v === undefined || v === '') return undefined
    const n = Number.parseInt(v, 10)
    return Number.isFinite(n) ? n : undefined
  })

// Schéma URL → coercion `?page=2` (string) → `2` (number) ; rejette les
// valeurs ≤ 0 (le BE est 1-based). Comme `idSchema` ci-dessus, on
// transform plutôt que `z.number()` parce que les `searchParams` sont
// toujours des strings.
const pageSchema = z
  .union([z.number().int().positive(), z.string()])
  .optional()
  .transform((v) => {
    if (v === undefined || v === '') return undefined
    if (typeof v === 'number') return v >= 1 ? v : undefined
    const n = Number.parseInt(v, 10)
    return Number.isFinite(n) && n >= 1 ? n : undefined
  })

export const workListFiltersSchema = z.object({
  q: z.string().optional(),
  tags: tagsSchema,
  series_id: idSchema,
  status: workStatusEnum.optional(),
  orientation: workOrientationEnum.optional(),
  sort: workSortEnum.optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  // Si true, ne renvoie que les œuvres avec ≥ 1 draft `status='pending'`.
  // Mutuellement exclusif avec `status` côté UI (les tabs WorksPage gèrent
  // la bascule), mais BE-side les deux peuvent coexister.
  has_pending_drafts: z.union([z.boolean(), z.literal('true').transform(() => true)]).optional(),
  // S5c — pagination 1-based dans l'URL (deep-linkable). `page_size`
  // n'est PAS dans l'URL : c'est une préférence locale (default 50,
  // ajustable plus tard via UI Settings si besoin).
  page: pageSchema,
})

export type WorkListFilters = z.output<typeof workListFiltersSchema>

// ──────────────────────────────────────────────────────────────────────────────
// Formulaire création (NewWorkPage)
// ──────────────────────────────────────────────────────────────────────────────

// Tier 2 — slugs validés par catalogue paramétrable S2.5c. Le BE valide à
// l'écriture qu'ils existent dans `emotional_intents` / `target_audiences` ;
// le FE ne contraint que la forme — les options du ChipMultiSelect viennent
// du fetch `useEmotionalIntents` / `useTargetAudiences`, déjà filtrées.
const slugString = z.string().min(1).max(80)

/**
 * Numeric optional — RHF avec `<input type="number">` retourne souvent ""
 * pour vide. Ce coerce traite "" / null / undefined comme `undefined` pour
 * éviter les "Expected number, got string" sans valeur.
 */
const optionalPositiveInt = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((v) => {
    if (v === undefined || v === null || v === '') return undefined
    const n = typeof v === 'number' ? v : Number(v)
    return Number.isFinite(n) ? n : undefined
  })
  .pipe(z.number().int().nonnegative().optional())

/**
 * Schéma de saisie atelier — partagé entre la création (`/works/new`) et
 * l'édition (`/works/:id/edit`). Tous les champs sont optionnels en draft :
 *  - `title_fr` reste min(1) pour la création (validé spécifiquement avant
 *    le 1er POST autosave) — en édition autosave, RHF ne PATCH le titre que
 *    si la valeur est valide (mode='onBlur').
 *  - `files` n'est pas dans le formulaire RHF : l'upload se fait par sa
 *    route dédiée `POST /works/:id/media` après la création du draft.
 */
export const workEditorFormSchema = z.object({
  title_fr: z.string().min(1, 'Le titre est requis').max(255),

  series_id: z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((v) => {
      if (v === undefined || v === null || v === '') return undefined
      const n = typeof v === 'number' ? v : Number(v)
      return Number.isFinite(n) ? n : undefined
    })
    .pipe(z.number().int().positive().optional()),
  series_position: optionalPositiveInt,

  description_fr: z.string().max(10_000).optional(),
  story_fr: z.string().max(500).optional(),

  emotional_intent: z.array(slugString).max(3).optional(),
  target_audience: z.array(slugString).max(3).optional(),
  technique_tags: z.array(z.string()).max(10).optional(),
  influences: z.array(z.string()).max(10).optional(),

  notes: z.string().max(10_000).optional(),

  // ISO YYYY-MM-DD natif (`<input type="date">`). Optional — le BE retombe
  // sur `captured_at_exif` extrait du 1er fichier.
  created_at_artistic: z.string().optional(),

  tag_ids: z.array(z.number().int()).default([]),
})

export type WorkEditorForm = z.input<typeof workEditorFormSchema>
export type WorkEditorFormParsed = z.output<typeof workEditorFormSchema>

// ──────────────────────────────────────────────────────────────────────────────
// Variantes — schéma RHF pour create/edit
//
// Refonte POD v6.1 : 1 variante = 1 format publiable (la junction multi-
// formats `work_variant_formats` a disparu). Le canal de vente est porté
// par `medium_kind` (politique unique). `printable_spec_id` (optionnel)
// lie la variante à un preset POD (domaine C). `work_media_id` (optionnel)
// lie à un master image dédié (matching ratio).
// ──────────────────────────────────────────────────────────────────────────────

const editionKindEnum = z.enum(['open', 'limited', 'original'])
const variantStatusEnum = z.enum(['draft', 'active', 'sold_out', 'archived'])
const mediumKindEnum = z.enum(['print', 'digital', 'original'])

const optionalIntFromString = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((v) => {
    if (v === undefined || v === null || v === '') return undefined
    const n = typeof v === 'number' ? v : Number(v)
    return Number.isFinite(n) && n > 0 ? n : undefined
  })
  .pipe(z.number().int().positive().optional())

export const variantFormSchema = z
  .object({
    medium_kind: mediumKindEnum,
    format_id: optionalIntFromString,
    work_media_id: optionalIntFromString,
    printable_spec_id: optionalIntFromString,
    framing_id: optionalIntFromString,
    support_id: optionalIntFromString,
    edition_kind: editionKindEnum,
    edition_size: optionalPositiveInt,
    // `price_cents` = NET atelier souhaité (option B refonte tarifs
    // 2026-05-15). EUR-only — colonne `currency` droppée par migration 012.
    price_cents: z.coerce.number().int().nonnegative(),
    // Nom commercial optionnel ("Collection Été 2026"). Vide → label dérivé
    // côté UI/BE depuis le format singulier (`label_fr` ou `WxH cm`).
    label_override: z.string().max(120).optional(),
    weight_g: optionalPositiveInt,
    packaged_width_cm: optionalPositiveInt,
    packaged_height_cm: optionalPositiveInt,
    packaged_depth_cm: optionalPositiveInt,
    fragile: z.boolean().default(true),
    stock_qty: optionalPositiveInt,
    status: variantStatusEnum.default('draft'),
  })
  // Garde-fous applicatifs miroirs des CHECK DB V3 (PR-D) :
  //   chk_wv_open_requires_spec    : open ⇒ printable_spec_id requis
  //   chk_wv_open_no_format        : open ⇒ format_id NULL
  //   chk_wv_limited_requires_format : limited ⇒ format_id requis
  //   chk_wv_limited_no_spec       : limited ⇒ printable_spec_id NULL
  //   chk_wv_digital_no_phys       : digital ⇒ pas de support/framing/spec
  // Le BE ré-applique ces invariants (defense in depth) ; FE pose un message
  // immédiat avant submit.
  .superRefine((data, ctx) => {
    // Digital : aucune matérialité physique.
    if (data.medium_kind === 'digital') {
      if (data.support_id !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['support_id'],
          message: 'Pas de support physique pour une variante digital',
        })
      }
      if (data.framing_id !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['framing_id'],
          message: "Pas d'encadrement pour une variante digital",
        })
      }
      if (data.printable_spec_id !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['printable_spec_id'],
          message: 'Une variante digital ne porte pas de preset POD',
        })
      }
      return
    }
    // Print / Original : dispatch par edition_kind.
    if (data.edition_kind === 'open') {
      // Option B 2026-05-18 — `printable_spec_id` n'est plus exigé côté
      // FE pour les variantes POD open : le BE auto-fill via le mapping
      // atelier `pod_matrix_default_spec_by_finish['no_frame']`. La
      // matrice POD bi-axe (auto-créée) route ensuite vers la bonne
      // spec par finition au publish. Si le mapping atelier est vide
      // ET qu'aucune spec Gelato n'existe, le BE relèvera l'erreur
      // actionnable (message pointant vers /settings).
      if (data.format_id !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['format_id'],
          message:
            'Une variante open ne porte pas de format spécifique ' +
            '(le preset POD gère les tailles)',
        })
      }
    } else if (data.edition_kind === 'limited') {
      if (data.format_id === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['format_id'],
          message: 'Format requis pour un tirage limité (1 taille fixe)',
        })
      }
      if (data.printable_spec_id !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['printable_spec_id'],
          message: 'Un tirage limité ne porte pas de preset POD (impression atelier)',
        })
      }
    } else if (data.edition_kind === 'original') {
      // Format optionnel ; spec interdite.
      if (data.printable_spec_id !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['printable_spec_id'],
          message: 'Une œuvre originale ne porte pas de preset POD',
        })
      }
    }
  })

export type VariantFormValues = z.input<typeof variantFormSchema>
export type VariantFormParsed = z.output<typeof variantFormSchema>

// ──────────────────────────────────────────────────────────────────────────────
// Politique éditoriale — seuils FR pour mise en ligne portfolio
// ──────────────────────────────────────────────────────────────────────────────

// keep in sync with backend/app/services/publication_service.py
//   PUBLISH_DESCRIPTION_FR_MIN_CHARS = 100
//   PUBLISH_STORY_FR_MIN_CHARS = 80
// Le test `__tests__/publication-thresholds-sync.test.ts` lit le fichier
// Python brut et fail si les valeurs divergent — la cohérence FE↔BE est
// vérifiée à chaque PR.
export const PORTFOLIO_PUBLISH_DESCRIPTION_FR_MIN_CHARS = 100
export const PORTFOLIO_PUBLISH_STORY_FR_MIN_CHARS = 80

export interface PortfolioMissingField {
  field: 'description_fr' | 'story_fr'
  minChars: number
  currentChars: number
  missingChars: number
}

export interface PortfolioPublishCheck {
  ready: boolean
  missing: PortfolioMissingField[]
}

/** Évalue si une œuvre passe les seuils FR pour la mise en ligne portfolio. */
export function evaluatePortfolioPublishability(work: {
  description_fr?: string | null
  story_fr?: string | null
}): PortfolioPublishCheck {
  const missing: PortfolioMissingField[] = []

  const desc = work.description_fr ?? ''
  if (desc.length < PORTFOLIO_PUBLISH_DESCRIPTION_FR_MIN_CHARS) {
    missing.push({
      field: 'description_fr',
      minChars: PORTFOLIO_PUBLISH_DESCRIPTION_FR_MIN_CHARS,
      currentChars: desc.length,
      missingChars: PORTFOLIO_PUBLISH_DESCRIPTION_FR_MIN_CHARS - desc.length,
    })
  }

  const story = work.story_fr ?? ''
  if (story.length < PORTFOLIO_PUBLISH_STORY_FR_MIN_CHARS) {
    missing.push({
      field: 'story_fr',
      minChars: PORTFOLIO_PUBLISH_STORY_FR_MIN_CHARS,
      currentChars: story.length,
      missingChars: PORTFOLIO_PUBLISH_STORY_FR_MIN_CHARS - story.length,
    })
  }

  return { ready: missing.length === 0, missing }
}
